import process from "node:process"

import { defineCommand } from "citty"
import consola from "consola"

import {
  checkClaudeVersion,
  updateClaude,
} from "./lib/claude-version-check"
import { runSelfUpdate } from "./lib/self-update"
import {
  injectPeerMcpIntoMirror,
  resolveCodexCliBackend,
  resolveGroupKeysFromMirror,
  writePeerMcpRuntimeFiles,
} from "./lib/codex-mcp-config"
import { enableFileLogging } from "./lib/file-log-reporter"
import { getCodexVersion, launchChild } from "./lib/launch"
import { listModelsForEndpoint } from "./lib/model-validation"
import { ensureClaudeConfigMirror, PATHS, removeOwnClaudeConfigMirror, writeArtifactCredsToMirror } from "./lib/paths"
import {
  buildArtifactOpenHookCommand,
  buildSessionBindHookCommand,
  buildStopHookCommand,
  captureLaunchBaseline,
  injectStopHookIntoSettingsFile,
  stopGateId,
  stopGateDisabled,
} from "./lib/orchestration/stop-gate-hook"
import {
  fileBaselineStore,
  repoFingerprint,
  repoRoot,
  stopGateEnabledForRepo,
  trustRepo,
} from "./lib/orchestration/stop-gate-policy"
import {
  checksForDescriptor,
  descriptorHash,
  parseGateDescriptor,
  type GateDescriptor,
} from "./lib/orchestration/harness-parse"
import {
  discoverGateCommands,
  readDiscoveredGate,
  sourcesHash,
  writeDiscoveredGate,
} from "./lib/orchestration/gate-discovery"
import { liveExec } from "./lib/orchestration/live-exec"
import { buildPromptSubmitHookCommand } from "./lib/orchestration/prompt-submit-hook"
import { ARTIFACT_REVIEW_SKILL, INJECTED_SKILLS, writeInjectedSkill } from "./lib/injected-skills"
import { shouldUseInsecureTls } from "./lib/artifact/tools"
import { parseBoolEnv } from "./lib/exec"
import nodePath from "node:path"
import { tmpdir } from "node:os"
import { randomBytes } from "node:crypto"
import { buildPeerAwarenessSnippet, type McpGroup } from "./lib/peer-mcp-personas"
import { appendPeerAwarenessToMirroredClaudeMd, appendToolbeltAwarenessToMirroredClaudeMd, prependArtifactPanelDirectiveToMirroredClaudeMd, prependStyleDirectiveToMirroredClaudeMd } from "./lib/claude-md-injection"
import { availableToolCommands, buildToolbeltAwareness, toolbeltEnabled } from "./lib/toolbelt"
import { provisionToolbelt } from "./lib/toolbelt/provision"
import { provisionAndIndexColbert } from "./lib/colbert"
import { startKeepAwake, stopKeepAwake } from "./lib/keep-awake"
import { provisionBrowserAssets } from "./lib/browser-mcp/provision"
import {
  DEFAULT_CLAUDE_MODEL_FALLBACKS,
  pickClaudeDefault,
} from "./lib/port"
import {
  agentToolsEnabled,
  browserToolsEnabled,
  fleetToolsEnabled,
  standInToolEnabled,
  workerToolsEnabled,
} from "./lib/mcp-capabilities"
import {
  getClaudeCodeEnvVars,
  parseSharedArgs,
  setupAndServe,
  sharedServerArgs,
} from "./lib/server-setup"
import { state } from "./lib/state"
import { resolveModel } from "./lib/utils"

export const claude = defineCommand({
  meta: {
    name: "claude",
    description: "Start the proxy server and launch Claude Code",
  },
  args: {
    ...sharedServerArgs,
    model: {
      alias: "m",
      type: "string",
      description:
        "Override the default model for Claude Code. Accepts a full slug (e.g. claude-opus-4-7) or an Opus family shorthand (e.g. 4.7, 4.8, 4.6) which expands to the best variant for that family — adding the [1m] suffix when a 1M-context backend is in the catalog.",
    },
    "codex-mcp": {
      type: "boolean" as const,
      default: true,
      description:
        "Wire peer-model MCP personas (codex-critic, codex-reviewer, gemini-critic) into the spawned Claude Code session",
    },
    "codex-cli": {
      type: "boolean" as const,
      default: false,
      description:
        "Add a `codex mcp-server` stdio backend so codex-implementer can mutate files. Requires codex CLI 0.129+; gracefully falls back to HTTP-only if absent.",
    },
    "codex-mcp-only": {
      type: "boolean" as const,
      default: false,
      description:
        "Pass --strict-mcp-config to claude code so only github-router's MCP servers are loaded (hides user's existing MCP servers)",
    },
    stealth: {
      type: "boolean" as const,
      default: false,
      description:
        "Opt back into VS Code-only beta header filtering. Loses leverage features (task budgets, token-efficient tools, prompt caching, etc.) but minimizes the wire-fingerprint difference from VS Code Copilot Chat. By default the `claude` subcommand enables extended/leverage betas because the spawned Claude Code already identifies itself via UA and other headers — partial stealth doesn't buy much.",
    },
    "trust-gate": {
      type: "boolean" as const,
      default: false,
      description:
        "Explicitly record consent for the structural Stop-gate in THIS repo (pinned to the repo's root-commit). The gate is ON BY DEFAULT when a harness is detected (consent-by-launching), so this is now mostly redundant; it stays for explicit/scripted use. Disable the gate entirely with GH_ROUTER_DISABLE_STOP_GATE=1.",
    },
    "no-stop-gate": {
      type: "boolean" as const,
      default: false,
      description:
        "Disable the structural Stop-gate for THIS session (same effect as GH_ROUTER_DISABLE_STOP_GATE=1). Intended for driven/automated sessions where a blocking Stop hook would hang the turn-end while a fleet driver waits.",
    },
    "auto-update": {
      type: "boolean" as const,
      default: true,
      description:
        "Check for and install the latest Claude Code on launch via `claude update` (throttled to once per hour via ~/.local/share/github-router/last-update-check). `claude update` respects the real install method (native installer or npm), so it never creates a conflicting second install; builds too old to support it fall back to `npm install -g @anthropic-ai/claude-code@latest`. Set to false (--no-auto-update) to check and warn only. Falls back gracefully if claude/npm/network unavailable.",
    },
    "update-check": {
      type: "boolean" as const,
      default: true,
      description:
        "Check the npm registry for a newer Claude Code version on launch and warn if stale (non-blocking ~500ms cost). Set to false (--no-update-check) to skip the check entirely (useful for offline/CI). Independent from --auto-update: --no-update-check implies no auto-install (nothing to install since we never check).",
    },
  },
  async run({ args }) {
    if (!process.stdout.isTTY) {
      consola.error("The claude subcommand requires a TTY (interactive terminal).")
      process.exit(1)
    }

    const parsed = parseSharedArgs(args as unknown as Record<string, unknown>)

    // Phase E P2.2: stealth-vs-leverage policy.
    // The `claude` subcommand defaults to LEVERAGE mode (extended-betas
    // ON) because the spawned Claude Code already identifies itself via
    // UA / editor-version / x-app headers — partial stealth doesn't
    // meaningfully reduce the wire fingerprint, and the cost of stealth
    // is losing features the user explicitly chose to install Claude
    // Code for (--max-budget-usd, token-efficient tools, prompt caching,
    // structured outputs, MCP, etc.).
    //
    // The `--stealth` flag opts back into the VS Code-only filter for
    // users who specifically want minimal wire diff over leverage.
    // The shared `--extended-betas` flag still works (treated as alias).
    //
    // Note: `advisor-tool-` is stripped in BOTH modes regardless of this
    // setting (Phase A: Copilot 400s on it). ADVISOR will be served via
    // Phase I's proxy-side translate path independently.
    if (args.stealth) {
      // Stealth wins if explicitly requested.
      parsed.extendedBetas = false
      consola.info(
        "Stealth mode: VS Code-only beta filtering. Leverage features disabled.",
      )
    } else if (!args["extended-betas"]) {
      // No explicit --extended-betas AND no --stealth → default ON.
      parsed.extendedBetas = true
    }
    // If user passed --extended-betas explicitly, parsed already reflects it.

    // Phase H P2: Claude Code version check + opt-in auto-update.
    // Default: check + auto-install if newer version available
    // (throttled to once per hour). The user explicitly chose to install
    // a Claude Code wrapper — they want the latest features and bug
    // fixes. Opt-out via --no-auto-update (check only, warn) or
    // --no-update-check (silence entirely). Best-effort: skips silently
    // if npm is offline or claude is not on PATH. The check happens
    // BEFORE setupAndServe so a stale version doesn't get spawned.
    if (args["update-check"] !== false) {
      try {
        const versionCheck = await checkClaudeVersion({
          noCheck: false,
        })
        if (versionCheck.skipped && versionCheck.skipReason === "no-claude") {
          // Claude isn't on PATH — let launchChild surface the more
          // contextual "claude not found" error in the spawn step.
          consola.debug(
            "claude --version probe failed; skipping auto-update.",
          )
        } else if (versionCheck.skipped && versionCheck.skipReason === "no-npm") {
          // npm view failed — likely offline. Don't block launch.
          consola.debug(
            "npm view @anthropic-ai/claude-code failed; skipping auto-update check (likely offline).",
          )
        } else if (
          versionCheck.needsUpdate
          && versionCheck.installedVersion
          && versionCheck.latestVersion
        ) {
          if (args["auto-update"] !== false) {
            try {
              await updateClaude(versionCheck.latestVersion)
            } catch (err) {
              const msg = err instanceof Error ? err.message : String(err)
              consola.warn(
                `Auto-update of Claude Code from ${versionCheck.installedVersion} to ${versionCheck.latestVersion} failed (${msg}); continuing with installed version. Run \`claude update\` (or \`npm install -g @anthropic-ai/claude-code@latest\`) manually to retry.`,
              )
            }
          } else {
            consola.warn(
              `Claude Code v${versionCheck.installedVersion} is installed; v${versionCheck.latestVersion} is available. Run with --auto-update (the default) to install on launch, or \`claude update\` manually.`,
            )
          }
        }
      } catch (err) {
        // Whole version-check should never block launch.
        consola.debug("Claude version check failed:", err)
      }
    }

    let server: Awaited<ReturnType<typeof setupAndServe>>["server"]
    let serverUrl: string
    try {
      const result = await setupAndServe({
        ...parsed,
        port: parsed.port, // undefined = random port
        silent: true,
      })
      server = result.server
      serverUrl = result.serverUrl
    } catch (error) {
      consola.error("Failed to start server:", error instanceof Error ? error.message : error)
      process.exit(1)
    }

    // Queue a best-effort self-update of github-router itself (detached,
    // applies next launch). Fire-and-forget so the bounded npm probe
    // never delays spawning Claude Code.
    void runSelfUpdate({ selfUpdate: args["self-update"] !== false })

    // Provision the router-owned CLAUDE_CONFIG_DIR with our synthetic
    // .credentials.json + a snapshot copy of the user's ~/.claude/.
    // The spawned Claude Code (and any teammates it spawns via the
    // agent-teams primitive) reads this dir instead of ~/.claude/,
    // finds our synthetic credential, and authenticates — closing the
    // teammate-spawn allowlist gap that drops ANTHROPIC_AUTH_TOKEN.
    // See ensureClaudeConfigMirror in src/lib/paths.ts.
    //
    // Run BEFORE enableFileLogging so a fatal credentials-write failure
    // surfaces on the user's terminal (we only throw on the credentials
    // write — copy failures of individual user files are debug-logged
    // and skipped).
    try {
      await ensureClaudeConfigMirror()
    } catch (err) {
      consola.error(
        `Failed to provision CLAUDE_CONFIG_DIR mirror: ${
          err instanceof Error ? err.message : String(err)
        }. Spawned Claude Code would not be able to authenticate.`,
      )
      process.exit(1)
    }

    enableFileLogging() // redirect errors/warnings to file; suppress terminal output

    // Two slugs flow through this code:
    //   * `chosenSlug` — the value we set for `ANTHROPIC_MODEL`. Must be an
    //     Anthropic-published slug (e.g. `claude-opus-4-8`) so Claude Code's
    //     hardcoded `/model` registry matches it and the UI shows the right
    //     menu entry. The proxy's resolver translates this back to a Copilot
    //     slug at request time, so the actual upstream call still works.
    //   * `resolvedSlug` — the Copilot-side slug after `resolveModel`. Used
    //     only for cache-presence validation (fallback chain) and the
    //     launch banner.
    //
    // For the implicit-default path only, we walk
    // DEFAULT_CLAUDE_MODEL_FALLBACKS when neither the default nor any
    // earlier fallback resolves to a model present in the Copilot cache.
    // Explicit `--model` is respected as-is — including Copilot slugs
    // (which Claude Code's UI won't recognize, but power users may want
    // for explicit pinning).
    // `-m 4.7` / `-m 4.8` shorthand: treat a bare "N.M" value as an
    // Opus-family preference and let pickClaudeDefault pick the best
    // variant for that family (adding [1m] when an opus-N.M-1m backend
    // exists in the catalog). Full slugs and any non-matching string
    // continue to pass through unchanged. `usingDefault` stays false
    // for shorthand so the DEFAULT_CLAUDE_MODEL_FALLBACKS walk doesn't
    // override an explicit family request with the next-older Opus.
    const usingDefault = !args.model
    const opusFamilyShorthand = args.model?.match(/^(\d+\.\d+)$/)?.[1]
    const requestedSlug = opusFamilyShorthand
      ? pickClaudeDefault(opusFamilyShorthand)
      : (args.model ?? pickClaudeDefault())
    let chosenSlug = requestedSlug
    let resolvedSlug = resolveModel(chosenSlug)

    if (usingDefault && state.models) {
      const inCache = (slug: string) =>
        state.models?.data.some((m) => m.id === resolveModel(slug)) ?? false
      if (!inCache(chosenSlug)) {
        for (const fallback of DEFAULT_CLAUDE_MODEL_FALLBACKS) {
          if (inCache(fallback)) {
            consola.info(
              `Default model "${chosenSlug}" not in your Copilot model list; falling back to "${fallback}".`,
            )
            chosenSlug = fallback
            resolvedSlug = resolveModel(fallback)
            break
          }
        }
      }
    }

    if (resolvedSlug !== chosenSlug) {
      consola.info(`Model "${chosenSlug}" resolved to "${resolvedSlug}"`)
    }
    const modelEntry = state.models?.data.find((m) => m.id === resolvedSlug)
    if (!modelEntry) {
      const available = listModelsForEndpoint("/v1/messages")
      consola.warn(
        `Model "${resolvedSlug}" not found. Available claude models: ${available.join(", ")}`,
      )
    }

    // Banner shows the round-trip so the user sees both names. Claude Code's
    // UI will display the chosenSlug; Copilot upstream sees resolvedSlug.
    const banner =
      chosenSlug === resolvedSlug
        ? chosenSlug
        : `${chosenSlug} → ${resolvedSlug}`
    // Print to stderr directly — consola's terminal reporter is already gone
    process.stderr.write(`Server ready on ${serverUrl}, launching Claude Code (${banner})...\n`)

    const envVars = getClaudeCodeEnvVars(serverUrl, chosenSlug)
    const extraArgs = ((args as unknown as Record<string, unknown>)._ as string[]) ?? []

    // LLM toolbelt: materialize curated CLI tools (rg/fd/jq/sd/sg/yq)
    // into the router bin dir prepended to the agent's PATH (the prepend
    // itself is done in getClaudeCodeEnvVars). Materialization runs in
    // the BACKGROUND so it never delays launch; the awareness one-liner
    // lists EVERY curated tool reachable this launch (system installs +
    // the toolbelt bin), appended to the mirrored CLAUDE.md so the main
    // agent AND descendants know which fast tools to prefer. Best-effort.
    if (toolbeltEnabled()) {
      void provisionToolbelt().catch((err) =>
        consola.debug("Toolbelt provisioning failed:", err),
      )
      const toolbeltLine = buildToolbeltAwareness(availableToolCommands())
      if (toolbeltLine) {
        try {
          await appendToolbeltAwarenessToMirroredClaudeMd(toolbeltLine)
        } catch (err) {
          consola.warn(
            `Toolbelt CLAUDE.md append failed: ${
              err instanceof Error ? err.message : String(err)
            }`,
          )
        }
      }
    }

    // Best-effort ColBERT semantic-search provision + background index of
    // the launch cwd (if a git repo). ON by default; never blocks launch,
    // never throws. Opt out with GH_ROUTER_DISABLE_SEMANTIC_SEARCH=1.
    void provisionAndIndexColbert()

    // Best-effort: keep the machine awake while the proxy/Claude Code
    // session runs (win32 default-on; opt out GH_ROUTER_DISABLE_KEEP_AWAKE=1).
    // Released via stopKeepAwake() in the shutdown chain below AND via the
    // module's own self-registered SIGINT/SIGTERM/exit reaper.
    startKeepAwake()

    // Best-effort: materialize the browser extension + bridge into the
    // stable app-dir and stamp the running version, so a one-time "Load
    // unpacked" survives npx/bunx upgrades. Gated on --browse; never
    // blocks launch, never throws.
    if (browserToolsEnabled()) {
      void provisionBrowserAssets().catch((err) =>
        consola.debug("Browser extension provisioning failed:", err),
      )
    }

    // Peer-MCP wiring. Default-on. When enabled:
    //   1. Decide between HTTP backend (always works, read-only personas)
    //      and the `--codex-cli` stdio backend (requires codex 0.129+,
    //      adds the implementer persona).
    //   2. Probe the live Copilot catalog for gemini-3.1-pro-preview.
    //   3. Generate a per-launch nonce, write the MCP config tempfile
    //      under PATHS.CLAUDE_RUNTIME_DIR with mode 0o600, AND write
    //      one .md subagent file per peer agent into ~/.claude/agents/
    //      (Phase 2.5 — `--agents` JSON does NOT populate Claude Code's
    //      Task subagent_type enum on v2.1.138; .md files in the canonical
    //      agents dir do).
    //   4. Inject `--mcp-config <path>` into the spawned Claude Code's
    //      argv. Add `--strict-mcp-config` if the user explicitly opts
    //      out of their existing MCP servers. The `--agents` JSON path
    //      is intentionally NOT passed: the .md registration in the
    //      canonical agents dir is the authoritative surface.
    //   5. Plumb `cleanup()` into launchChild's onShutdown so tempfiles
    //      are unlinked on signal exit.
    //
    // The per-launch CLAUDE_CONFIG_DIR mirror is ALWAYS cleaned up on
    // shutdown (regardless of codex-mcp), since `ensureClaudeConfigMirror`
    // above always provisioned it. We chain the peer-MCP cleanup
    // (if any) ahead of the mirror removal so files inside the mirror
    // get unlinked first via known paths; the recursive `fs.rm` is
    // belt-and-braces for everything else.
    const baseShutdown = async (): Promise<void> => {
      await stopKeepAwake()
      await removeOwnClaudeConfigMirror()
    }
    let onShutdown: () => Promise<void> = baseShutdown
    const codexMcpEnabled = (args as Record<string, unknown>)["codex-mcp"] !== false
    if (codexMcpEnabled) {
      try {
        const requestedCli =
          ((args as Record<string, unknown>)["codex-cli"] as boolean | undefined) ?? false
        const backend = resolveCodexCliBackend({
          requested: requestedCli,
          codexInfo: requestedCli ? getCodexVersion() : null,
        })
        const geminiAvailable =
          state.models?.data.some((m) => /^gemini-3\..*pro/i.test(m.id)) ?? false
        if (!geminiAvailable) {
          consola.info(
            "gemini-3.1-pro-preview not found in your Copilot model catalog; gemini-critic persona will not be registered.",
          )
        }

        // Which scoped MCP servers to register. `peers` + `search` are
        // always on; `workers` / `decide` / `browser` only when their gate
        // passes at launch (avoids registering a server whose tools would
        // all be filtered out of tools/list). Resolve each group's
        // `.claude.json` config key ONCE (bare, or `gh-router-<group>` on
        // collision) and thread it into every channel — the mcpServers
        // entries, the persona .md routing strings, and the awareness
        // snippet — so a user-side `browser`/`search` MCP never silently
        // hijacks or drops one of ours.
        const enabledGroups: Array<McpGroup> = ["peers", "search", "orchestrate"]
        if (workerToolsEnabled()) enabledGroups.push("workers")
        if (standInToolEnabled()) enabledGroups.push("decide")
        if (browserToolsEnabled()) enabledGroups.push("browser")
        if (fleetToolsEnabled()) enabledGroups.push("fleet")
        if (agentToolsEnabled()) enabledGroups.push("first-mate")
        const { keys: groupKeys, skipped: skippedGroups } =
          await resolveGroupKeysFromMirror(enabledGroups)

        const runtime = await writePeerMcpRuntimeFiles(serverUrl, {
          codexCli: backend === "cli",
          geminiAvailable,
          groupKeys,
        })
        state.peerMcpNonce = runtime.nonce
        // Reach-back channel for the advisory-review hooks (hook V2): the
        // detached Stop reviewer and the UserPromptSubmit hook run as separate
        // processes with no in-proc state, so they call the proxy over loopback
        // HTTP using this URL + the per-launch nonce (the same nonce the /mcp
        // handler validates). Set on the spawned child's env so the hooks — and
        // the detached reviewer they spawn — inherit it.
        envVars.GH_ROUTER_HOOK_MCP_URL = serverUrl
        envVars.GH_ROUTER_HOOK_NONCE = runtime.nonce
        onShutdown = async (): Promise<void> => {
          await runtime.cleanup()
          await baseShutdown()
        }

        // Subagent MCP visibility: inject `gh-router-peers` (and the
        // `codex-cli` stdio entry when enabled) into the mirrored
        // `<CLAUDE_CONFIG_DIR>/.claude.json` so subagents — Agent-tool
        // subagents, forks, agent-teams subprocesses — discover the peer
        // MCP from persistent (user-scope) config rather than the parent's
        // ephemeral --mcp-config CLI flag. Same nonce as runtime files
        // (the proxy validates Authorization against the launch nonce
        // regardless of which channel the request came through).
        //
        // On collision with a user-side entry of the same name, this
        // returns ok:false; we then keep --mcp-config as the fallback so
        // at least the parent session retains the peer tools (subagents
        // remain blind in that case, by design — explicit branch, not
        // silent precedence).
        const injected = await injectPeerMcpIntoMirror(serverUrl, {
          codexCli: backend === "cli",
          geminiAvailable,
          groupKeys,
          nonce: runtime.nonce,
        })

        // Channel selection: prefer the mirror (subagent-visible) when
        // injection succeeded. Only fall back to --mcp-config when
        // injection refused due to a user-side collision. Pushing BOTH
        // would register the same server name twice (mirror + CLI flag),
        // which is ambiguous across Claude Code versions.
        if (!injected.ok) {
          extraArgs.push("--mcp-config", runtime.mcpConfigPath)
          if ((args as Record<string, unknown>)["codex-mcp-only"] === true) {
            extraArgs.push("--strict-mcp-config")
          }
        } else if ((args as Record<string, unknown>)["codex-mcp-only"] === true) {
          // User asked for strict-MCP-only but the mirror inject path
          // can't enforce that (other user-scope MCPs already in the
          // mirror's snapshot are visible). Warn so the flag's mismatch
          // with the new behavior is obvious.
          consola.warn(
            "--codex-mcp-only has no effect when peer MCP is wired via the "
              + "mirrored .claude.json (the user's existing user-scope MCPs in "
              + "the snapshot are still visible). Pass --no-codex-mcp to skip "
              + "peer-MCP wiring entirely.",
          )
        }

        const personaNames = runtime.personas.map((p) => p.agentName).join(", ")
        const subagentVisibility = injected.ok
          ? `subagent-visible (mirrored mcpServers: [${injected.serversAdded.join(", ")}])`
          : `subagent-INVISIBLE (collision on user-side mcpServers: [${injected.conflictingServers.join(", ")}]; parent-only via --mcp-config)`
        const skippedNote =
          skippedGroups.length > 0
            ? ` WARNING: groups [${skippedGroups.join(", ")}] skipped — both the bare and \`gh-router-<group>\` keys collide with your own mcpServers; those tools are unavailable this session (rename the user-side server to re-enable).`
            : ""
        process.stderr.write(
          `Peer MCP wired (backend=${backend}, personas=[${personaNames}], `
            + `subagent .md files=${runtime.agentMdPaths.length}, ${subagentVisibility}).${skippedNote}\n`,
        )

        // ── Floor-raising agent surface (docs/floor-raising-agent-surface.md) ──
        // NOTE (intentional coupling, gemini integration review): this block is
        // nested in `if (codexMcpEnabled)` — the github-router "enhancement
        // layer" master switch — alongside the MCP server injection above. The
        // skills REQUIRE that injection (they call mcp__workers__* /
        // mcp__orchestrate__* / mcp__search__* tools), so coupling them here is
        // correct. The Stop-gate is MCP-independent and could live outside;
        // keeping it here means `--no-codex-mcp` opts out of the whole layer
        // (consistent: that flag also hides --trust-gate, so the gate was never
        // enabled in such a session anyway). Decoupling the Stop-gate + moving
        // the per-prompt reset out is a clean follow-up.
        // When the worker/orchestrate backend is available, materialize the
        // injected skills into the mirror and register the front-end
        // UserPromptSubmit hook (per-prompt Stop-gate budget reset + advisory
        // goal steer). gh-first-mate is additionally gated on the first-mate MCP
        // server so the slash command does not advertise an unavailable tool.
        // The hooks scope themselves to the top-level session via the payload's
        // agent_type, so subagents/teammates are untouched.
        const sessionCwd = process.cwd()
        if (workerToolsEnabled()) {
          const skillsToWrite = INJECTED_SKILLS.filter(
            (s) => s.name !== "gh-first-mate" || agentToolsEnabled(),
          )
          let skillsWritten = 0
          for (const s of skillsToWrite) {
            const r = await writeInjectedSkill(s.name, s.md).catch(() => ({ written: false }))
            if (r.written) skillsWritten++
          }
          try {
            const settingsPath = nodePath.join(PATHS.CLAUDE_CONFIG_DIR, "settings.json")
            const cmd = buildPromptSubmitHookCommand(process.execPath, process.argv[1])
            // Raise the host hook timeout to 45s (default 30s): the V2 path may
            // make one gpt-5.5 scope call + a parallel code search. The hook's
            // own enrichment is bounded well under this (≈22s) and fails open,
            // so 45s is headroom, not a tax the user routinely pays.
            await injectStopHookIntoSettingsFile(settingsPath, cmd, "UserPromptSubmit", 45)
          } catch (err) {
            consola.warn(`Could not register the UserPromptSubmit hook: ${String(err)}`)
          }
          if (skillsWritten > 0) {
            const skillNames = skillsToWrite.map((s) => `/${s.name}`).join(", ")
            process.stderr.write(
              `Injected skills (${skillsWritten}/${skillsToWrite.length}): ${skillNames}.\n`,
            )
          }
        }

        // ai-or-die session binding. When launched inside an ai-or-die Terminal
        // tab, ai-or-die sets AIORDIE_CLAUDE_BIND to a per-tab sidecar path.
        // Register a SessionStart/SessionEnd hook that records the active claude
        // session id + transcript path to that sidecar on every startup / resume
        // / clear / compact, so ai-or-die can bind the tab's sticky-note
        // summariser to the exact transcript (surviving /resume, /clear and
        // exit→relaunch). The path is baked into the hook command (not env), and
        // AIORDIE_CLAUDE_BIND is stripped from the child's env (sanitizeParentEnv),
        // so a nested `github-router claude` can't hijack the parent tab; the hook
        // itself also skips subagent/teammate payloads (agent_id/agent_type).
        const aiordieSidecar = (process.env.AIORDIE_CLAUDE_BIND ?? "").trim()
        // The path is baked into the hook command string (double-quoted, like
        // buildStopHookCommand). It is app-controlled (ai-or-die's data dir + a
        // uuid), so it never contains a double-quote; refuse defensively if it
        // somehow does rather than emit a malformed command (ai-or-die then falls
        // back to its inference path).
        if (aiordieSidecar.length > 0 && !aiordieSidecar.includes('"')) {
          try {
            const settingsPath = nodePath.join(PATHS.CLAUDE_CONFIG_DIR, "settings.json")
            const command = buildSessionBindHookCommand(process.execPath, process.argv[1], aiordieSidecar)
            await injectStopHookIntoSettingsFile(settingsPath, command, "SessionStart")
            await injectStopHookIntoSettingsFile(settingsPath, command, "SessionEnd")
          } catch (err) {
            consola.warn(`Could not register the ai-or-die session-bind hook: ${String(err)}`)
          }
        }

        // Default-on plan review in the artifact panel: when inside an ai-or-die
        // tab (AIORDIE_SESSION_ID set), the artifact_* tools reach a real review
        // panel, so materialize the gh-artifact-review skill and steer the agent
        // to open plans there by default. Skill is written here (not in
        // INJECTED_SKILLS) so it only appears inside a tab; the CLAUDE.md
        // directive reaches descendants. Both best-effort.
        if ((process.env.AIORDIE_SESSION_ID ?? "").trim().length > 0) {
          await writeInjectedSkill(ARTIFACT_REVIEW_SKILL.name, ARTIFACT_REVIEW_SKILL.md)
            .catch(() => ({ written: false }))
          try {
            await prependArtifactPanelDirectiveToMirroredClaudeMd()
          } catch (err) {
            consola.warn(`Artifact-panel directive prepend failed: ${String(err)}`)
          }
          // Hands-off auto-open: write the artifact creds to a mode-600 mirror
          // file (AIORDIE_TOKEN is stripped from the child env, and argv leaks to
          // ps), then register a PostToolUse(ExitPlanMode) hook so the finalized
          // plan opens in the panel without the model calling artifact_open.
          try {
            await writeArtifactCredsToMirror(shouldUseInsecureTls(process.env.AIORDIE_BASE_URL ?? ""))
            const settingsPath = nodePath.join(PATHS.CLAUDE_CONFIG_DIR, "settings.json")
            const cmd = buildArtifactOpenHookCommand(process.execPath, process.argv[1])
            await injectStopHookIntoSettingsFile(settingsPath, cmd, "PostToolUse", undefined, "ExitPlanMode")
          } catch (err) {
            consola.warn(`Could not register the artifact auto-open hook: ${String(err)}`)
          }
        }

        // Structural-gate Stop hook — CONSENT-GATED per repo (default OFF; it
        // never runs an UNTRUSTED repo's scripts). `--trust-gate` records consent
        // for this repo; GH_ROUTER_ENABLE_STOP_GATE force-enables. The hook also
        // re-checks trust at runtime and scopes to the top-level session.
        if ((args as Record<string, unknown>)["trust-gate"] === true) {
          try {
            const root = await trustRepo(sessionCwd)
            process.stderr.write(
              `Structural gate trusted for this repo (${root}); it will run on launch here from now on.\n`,
            )
          } catch (err) {
            consola.warn(`Could not record gate trust: ${String(err)}`)
          }
        }
        const gateDisabled = stopGateDisabled(args as Record<string, unknown>)
        const forceEnabled = parseBoolEnv(process.env.GH_ROUTER_ENABLE_STOP_GATE) === true
        const includeTests = parseBoolEnv(process.env.GH_ROUTER_STOP_GATE_RUN_TESTS) === true
        const gateRoot = await repoRoot(sessionCwd).catch(() => sessionCwd)
        // Resolve the gate source: the deterministic parser first (the bun/TS
        // sealed fast-path OR a parsed multi-language check set), then the cached
        // model-discovered record. A cache MISS marks `discovering` (background
        // discovery arms the gate on the NEXT launch — no launch-time model
        // latency). Discovery is OPT-IN (GH_ROUTER_ENABLE_GATE_DISCOVERY=1): it
        // runs a read-only worker that reads repo files, so it stays off by
        // default; the parser covers the common ecosystems with no model at all.
        let descriptor: GateDescriptor | null = null
        let discovering = false
        if (!gateDisabled) {
          descriptor = await parseGateDescriptor(gateRoot, { includeTests }).catch(() => null)
          if (!descriptor) {
            const cached = await readDiscoveredGate(gateRoot).catch(() => null)
            if (cached) {
              descriptor = {
                kind: "discovered",
                checks: cached.checks,
                ecosystem: cached.ecosystem,
                workdir: gateRoot,
                evidence: cached.evidence,
              }
              // Force-enable wants a gate NOW (not a next-launch background discovery);
              // when nothing parsed/cached, it falls back to the sealed default below.
            } else if (!forceEnabled && workerToolsEnabled() && parseBoolEnv(process.env.GH_ROUTER_ENABLE_GATE_DISCOVERY) === true) {
              discovering = true
            }
          }
        }
        // Enable when forced-on, already trusted, or auto-trust-on-detection (a
        // resolved descriptor OR a kicked discovery is consent-by-launching). The
        // gate only ever runs the launched repo's own checks and is
        // baseline-isolated; opt out entirely with GH_ROUTER_DISABLE_STOP_GATE=1.
        let gateEnabled = gateDisabled ? false : await stopGateEnabledForRepo(sessionCwd).catch(() => false)
        let autoTrusted = false
        if (!gateEnabled && !gateDisabled && (descriptor || discovering)) {
          try {
            await trustRepo(sessionCwd)
            gateEnabled = true
            autoTrusted = true
          } catch (err) {
            consola.warn(`Could not auto-trust this repo for the structural gate: ${String(err)}`)
          }
        }
        // Force-enable (GH_ROUTER_ENABLE_STOP_GATE / --trust-gate) with no parsed
        // or discovered descriptor falls back to the sealed default gate, matching
        // the legacy behavior of explicit enablement (takes precedence over a
        // background discovery, which `forceEnabled` already suppressed above).
        if (gateEnabled && !descriptor && !discovering) {
          descriptor = { kind: "sealed", gateId: stopGateId(), workdir: gateRoot }
        }
        if (gateEnabled && descriptor) {
          const armed = descriptor
          try {
            const settingsPath = nodePath.join(PATHS.CLAUDE_CONFIG_DIR, "settings.json")
            const command = buildStopHookCommand(process.execPath, process.argv[1])
            // Arm the matching runtime resolver (env reaches the child via envVars).
            if (armed.kind === "sealed") envVars.GH_ROUTER_STOP_GATE_ID = armed.gateId
            else if (armed.kind === "parsed") envVars.GH_ROUTER_STOP_GATE_PARSED = "1"
            else envVars.GH_ROUTER_STOP_GATE_DISCOVERED = "1"
            if (includeTests) envVars.GH_ROUTER_STOP_GATE_RUN_TESTS = "1"
            await injectStopHookIntoSettingsFile(settingsPath, command)
            // Capture the baseline at LAUNCH (pre-mutation) for the dynamic paths,
            // so an agent-introduced failure is a regression — not silently adopted
            // as the baseline at first stop. Sealed keeps its legacy first-stop
            // baseline (unchanged). Fire-and-forget: never blocks the launch.
            if (armed.kind !== "sealed") {
              const checks = checksForDescriptor(armed)
              const descriptorKey = descriptorHash(armed)
              // Per-launch token isolates concurrent sessions' baselines; the child
              // reads it back to recompute the same launch-baseline key.
              const token = randomBytes(8).toString("hex")
              envVars.GH_ROUTER_STOP_GATE_BASELINE_TOKEN = token
              void (async () => {
                await captureLaunchBaseline({
                  checks,
                  workdir: armed.workdir,
                  exec: liveExec,
                  descriptorKey,
                  token,
                  baseline: fileBaselineStore(nodePath.join(tmpdir(), "gh-router-stopgate-baseline")),
                })
              })().catch(() => {})
            }
            const tail =
              "A regression or a gate-weakening diff blocks stopping until fixed (per-prompt, max 2). "
              + "Opt out with GH_ROUTER_DISABLE_STOP_GATE=1.\n"
            if (armed.kind === "sealed") {
              process.stderr.write(
                (autoTrusted
                  ? `Structural-gate Stop hook enabled by default for this repo (gate=${armed.gateId}; runs typecheck/test/lint at stop). `
                  : `Structural-gate Stop hook enabled (gate=${armed.gateId}). `) + tail,
              )
            } else {
              const ids = checksForDescriptor(armed).map((c) => c.id).join(", ")
              const testNote = includeTests
                ? "Full test suite ON. "
                : "Full test suite off by default (enable with GH_ROUTER_STOP_GATE_RUN_TESTS=1). "
              process.stderr.write(
                `Structural-gate Stop hook armed for this ${armed.ecosystem} repo (checks: ${ids}; `
                  + `from ${armed.evidence.join(", ")}). ${testNote}${tail}`,
              )
            }
          } catch (err) {
            // Never fail the launch over the hook; surface and continue.
            consola.warn(`Could not register the structural-gate Stop hook: ${String(err)}`)
          }
        } else if (gateEnabled && discovering) {
          // Kick a background discovery; the gate arms on the next launch.
          process.stderr.write(
            "Structural Stop-gate: no checks parsed for this repo — discovering check commands in the "
              + "background (arms on the next launch). Disable with GH_ROUTER_ENABLE_GATE_DISCOVERY=0.\n",
          )
          void (async () => {
            try {
              const disc = await discoverGateCommands(gateRoot, {
                includeTests,
                signal: AbortSignal.timeout(120_000),
              })
              if (disc) {
                const [fingerprint, sh] = await Promise.all([
                  repoFingerprint(gateRoot).catch(() => ""),
                  sourcesHash(gateRoot).catch(() => ""),
                ])
                await writeDiscoveredGate({
                  root: gateRoot,
                  fingerprint,
                  sourcesHash: sh,
                  discoveredAt: new Date().toISOString(),
                  model: "gemini-3.5-flash",
                  ecosystem: disc.ecosystem,
                  checks: disc.checks,
                  confidence: disc.confidence,
                  evidence: disc.evidence,
                })
              }
            } catch {
              /* best-effort: discovery never blocks or fails the launch. */
            }
          })().catch(() => {})
        } else if (!gateDisabled && !descriptor && !discovering) {
          // The original "silent skip" fix: say WHY the gate is off.
          process.stderr.write(
            "Structural Stop-gate not enabled: no checks found in this repo "
              + "(looked at package.json scripts / CI workflows / Makefile / Cargo.toml / go.mod / pyproject). "
              + "Force a sealed gate with GH_ROUTER_ENABLE_STOP_GATE=1"
              + (workerToolsEnabled() ? ", or let a model discover them with GH_ROUTER_ENABLE_GATE_DISCOVERY=1" : "")
              + ".\n",
          )
        }

        // Awareness snippet: append a short, descriptive system-prompt
        // section telling Claude *what* peer-review tools exist — Claude
        // decides *whether* to call them based on each tool's own
        // `description` (the routing layer). This is the awareness layer.
        //
        // Delivery is dual-surface, both unconditional:
        //   1. --append-system-prompt — system-turn position, strongest
        //      attention weight; reaches the main agent only.
        //   2. <CLAUDE_CONFIG_DIR>/CLAUDE.md (appended) — user-turn
        //      <claudeMd> wrapper; reaches Agent-tool subagents and
        //      agent-teams teammates that inherit CLAUDE_CONFIG_DIR but
        //      not --append-system-prompt.
        //
        // Capability gates: worker_* and stand_in mentions are gated on
        // the same predicates the /mcp tools/list uses (workerToolsEnabled
        // / standInToolEnabled), so the snippet never names a tool that
        // is missing from the live catalog.
        //
        // Previously gated by GH_ROUTER_PEER_AWARENESS (default-on).
        // The flag was dropped (per plan: it served no purpose now that
        // the snippet is default-on across both surfaces). Existing
        // `GH_ROUTER_PEER_AWARENESS=0` shell exports are silent no-ops.
        const peerSnippet = buildPeerAwarenessSnippet({
          codexCli: backend === "cli",
          geminiAvailable,
          workerToolsAvailable: workerToolsEnabled(),
          standInAvailable: standInToolEnabled(),
          browseAvailable: state.browseEnabled,
          powerBrowseAvailable: state.powerBrowseEnabled,
          agentToolsAvailable: agentToolsEnabled(),
          groupKeys,
        })
        extraArgs.push("--append-system-prompt", peerSnippet)
        // Ordering invariant: this MUST run AFTER ensureClaudeConfigMirror()
        // has resolved (above in this same handler), so the snapshot of
        // the user's ~/.claude/CLAUDE.md is already in place before we
        // append our marker block. The helper's own mirror-only safety
        // guard rejects writes outside CLAUDE_CONFIG_DIR as defence in
        // depth. Failures warn-and-continue — the main agent already has
        // the awareness via --append-system-prompt, so this surface is
        // descendant-reach enhancement, not a launch-blocker.
        try {
          await appendPeerAwarenessToMirroredClaudeMd(peerSnippet)
        } catch (err) {
          consola.warn(
            `Peer-awareness CLAUDE.md append failed (main agent still covered via --append-system-prompt): ${
              err instanceof Error ? err.message : String(err)
            }`,
          )
        }
        // Style directive: prepend a short writing/communication style
        // directive at the TOP of the mirrored CLAUDE.md so every
        // spawned agent reads it first. Independent marker fence from
        // the peer-awareness block, so the two coexist (style at top,
        // user content in the middle, peer-awareness at the bottom).
        // Best-effort like the append above.
        try {
          await prependStyleDirectiveToMirroredClaudeMd()
        } catch (err) {
          consola.warn(
            `Style-directive CLAUDE.md prepend failed: ${
              err instanceof Error ? err.message : String(err)
            }`,
          )
        }
      } catch (err) {
        consola.warn(
          `Peer MCP wiring failed (claude will launch without it): ${
            err instanceof Error ? err.message : String(err)
          }`,
        )
      }
    }

    launchChild(
      { kind: "claude-code", envVars, extraArgs, model: chosenSlug },
      server,
      { onShutdown },
    )
  },
})
